import React from "react";
import dayjs from "dayjs";
import { useFunnelNavigate } from "src/common/hooks/useFunnelNavigate.hook";
import { CustomerInterface, i18n, saveCustomer, setGlobalLoading, setSoIdWeb, showMessage, SourceEnum, useAppDispatch, useCreateSoWithCaptcha, useForm } from "src/common";
import { createSoSupplementary } from "src/common/api/apollo/supplementary";
import { ArrowRightIcon, Button, CaptchaChallenge, Flex, InputBirthDate, InputFullName, InputPhoneNumber, InputText, RadioButtonGroupInput } from "src/components";
import { AppRoutes } from "src/pages/app";
import { colors } from "src/theme";
import { PartnerEnum } from "src/common/enums";
import { FieldConfig, Form, FormConfig } from "./form";
import { Validation } from "./validation";

export type CustomerFormItemType = 'fullName' | 'birthDate' | 'email' | 'phoneNumber' | 'hasPolicy';
export const allCustomerFormItems: Array<CustomerFormItemType> = ['fullName', 'birthDate', 'email', 'phoneNumber', 'hasPolicy'];

interface CustomerFormProps {
  customer?: CustomerInterface;
  partnerSource?: PartnerEnum;
  formItems?: Array<CustomerFormItemType>;
  labels?: Partial<Record<CustomerFormItemType, string>>;
}

// Primjer korištenja komponente forme
export const CustomerForm: React.FC<CustomerFormProps> = ({ customer, partnerSource, formItems = allCustomerFormItems, labels }) => {
  const { formRef, submitForm } = useForm();
  const dispatch = useAppDispatch();
  const navigate = useFunnelNavigate();

  // createSo behind health-api's captcha ladder: past the free tier the API answers
  // CAPTCHA_REQUIRED, the hook renders the widget and resends the identical payload
  // with the token. The handlers below run on whichever attempt finally settles.
  const { submit: createSo, captcha } = useCreateSoWithCaptcha(createSoSupplementary, { page: 'dzo-customer' });

  const handleSubmit = (values: { [key: string]: any }) => {
    dispatch(setGlobalLoading(true));
    const data: CustomerInterface = {
      ...values,
      age: dayjs().diff(dayjs(values.birthDate), 'year')
    }
    createSo(
      {
        input: {
          dateOfBirth: data.birthDate,
          email: data.email,
          phoneNumber: data.phoneNumber,
          productTypeCode: 'dzo',
          source: SourceEnum.DOPUNSKO,
          partnerSource,
          hasPolicy: values.hasPolicy
        }
      },
      {
        onSuccess: (response) => {
          const soId = response.data.createSo;
          dispatch(saveCustomer(data));
          dispatch(setSoIdWeb(soId));
          navigate(AppRoutes.OFFERS.fullPath);
        },
        onError: (error) => {
          console.error('Greška prilikom izvršavanja mutacije:', error);
          // Without this the funnel silently dead-ends here: the customer stays on a
          // filled-in form with no indication that anything went wrong. A rate-limit
          // denial gets its own toast from the Apollo error link, so don't double up.
          const isRateLimited = error?.graphQLErrors?.some(
            (gqlError: { extensions?: { code?: string } }) =>
              gqlError.extensions?.code === 'RATE_LIMITED' || gqlError.extensions?.code === 'IP_BLOCKED'
          );
          if (!isRateLimited) {
            dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 8000 }));
          }
        },
        onSettled: () => {
          dispatch(setGlobalLoading(false));
        },
      },
    );
  };

  const fields: FieldConfig[] = [
    {
      name: 'fullName',
      label: labels?.fullName ?? i18n.t('components.forms.customerForm.fullName'),
      component: InputFullName,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validFullName', message: 'Unesi ispravno ime i prezime' },
      ] as Validation[],
    },
    {
      name: 'birthDate',
      label: labels?.birthDate ?? i18n.t('components.forms.customerForm.dateOfBirth'),
      component: InputBirthDate,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validDate', message: 'Unesi ispravan datum rođenja' },
      ] as Validation[],
    },
    {
      name: 'email',
      label: labels?.email ?? i18n.t('components.forms.customerForm.mail'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50
      },
      validations: [
        { type: 'required', message: 'Polje je obavezno.' },
        { type: 'validMail', message: 'Unesite ispravnu e-mail adresu.' },
        { type: 'validMailNoDiacritics', message: 'Dio e-mail adrese prije @ ne smije sadržavati č, ć, ž, š ni đ.' },
      ] as Validation[],
    },
    {
      name: 'phoneNumber',
      label: labels?.phoneNumber ?? i18n.t('components.forms.customerForm.phone'),
      component: InputPhoneNumber,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validPhoneNumber', message: 'Unesi ispravan broj mobitela', params: { regionCode: 'countryCode' } },
      ] as Validation[],
    },
    {
      name: 'hasPolicy',
      label: labels?.hasPolicy ?? i18n.t('components.forms.customerForm.hasPolicy'),
      component: RadioButtonGroupInput,
      componentProps: {
        options: [{ label: 'Da', value: true }, { label: 'Ne', value: false }],
        horizontal: false,
      },
      validations: [
        { type: 'requiredChoice', message: 'Odaberite odgovor' },
      ] as Validation[],
    }
  ].filter((field) => formItems.includes(field.name as CustomerFormItemType));

  const formConfig: FormConfig = {
    gap: 24
  };

  return <Flex vertical gap={24}>
    <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} initialValues={customer} noValidate />
    {/* Appears only after the API asked for a captcha; solving it resubmits, so the button waits. */}
    <CaptchaChallenge {...captcha} />
    <Button btnType="primary" iconPosition="right" text={i18n.t('components.forms.customerForm.submit')} style={{ width: '100%' }} disabled={captcha.required} onClick={() => submitForm()}>
      <ArrowRightIcon color={colors.white} />
    </Button>
  </Flex>
};
